import type { PlaybackMode } from '../database/custom-types/playback-mode'
import type { TAudienceWebRtcIceServer } from './audience-webrtc-ice'

import type { TRemoteOverlayUi } from './player-overlay-ui'

import type { IWebOtaUpdateEventPayload } from './player-update'
import type { TPlayerWeeklyAvailabilitySchedule } from './player-weekly-availability'
import type { TFrame } from './playlist'
import type { TWebOtaApplyTiming } from './tenant'
import type { ITimeBlock } from './time-block'
import { groupRegistrationPlaylistsToScheduleRoots } from '../helpers/registrationPlaylists'

/** White-label assets for player idle / waiting chrome. */
export interface ITenantPlayerBranding {
  /**
   * Effective idle-screen logo URN (player-specific logo when set, else company profile logo).
   * Omitted when neither is configured.
   */
  logoUrn?: string | null
  /** Full-screen idle background image URN; omitted when unset (default Lucimark chrome). */
  backgroundUrn?: string | null
}

/** Tenant metadata included in channel payload. */
export interface ITenantInfo {
  id: string
  name?: string
  quotaExcess: boolean
  /**
   * When true, player should send proof-of-play data with playback heartbeat.
   * From tenant effective PoP entitlement (`proofOfPlay` || Book). Older APIs may omit; treat omitted as on while registered.
   */
  proofOfPlay?: boolean
  /**
   * Web OTA apply policy for this tenant (`immediate` = download and apply ASAP; `next_app_restart` = pre-download/cache, apply after next restart). Omitted for unregistered players (treat as immediate).
   * Mirrors tenant.settings.playerWebOtaApplyTiming with default immediate.
   */
  webOtaApplyTiming?: TWebOtaApplyTiming
  /**
   * When true, player should not show on-screen operator notifications (campaign updated, list sync change).
   * Mirrors tenant.settings.playerOperatorNotificationsMuted; default false when omitted.
   */
  playerOperatorNotificationsMuted?: boolean
  /**
   * When true, player should only play frames whose layout aspect ratio matches the viewport.
   * Mirrors tenant.settings.playerRestrictPlaybackToDeviceAspectRatio; default true when omitted (resolved server-side).
   */
  playerRestrictPlaybackToDeviceAspectRatio?: boolean
  /**
   * When true, player dissolves between creatives (including warm stills).
   * Mirrors tenant.settings.playerCreativeCrossfade; default true when omitted (resolved server-side).
   */
  playerCreativeCrossfade?: boolean
  /**
   * Dissolve duration in ms when crossfade is on.
   * Mirrors tenant.settings.playerCreativeCrossfadeMs; default 300 when omitted (resolved server-side).
   */
  playerCreativeCrossfadeMs?: number
  /**
   * When true, low-capacity devices may dissolve (follows playerCreativeCrossfade).
   * Mirrors tenant.settings.playerCreativeCrossfadeOnLowCapacity; default false when omitted.
   */
  playerCreativeCrossfadeOnLowCapacity?: boolean
  /**
   * When true, low-capacity devices play video. When false, skip frames that contain video.
   * Mirrors tenant.settings.playerAllowVideoOnLowCapacity; default false when omitted.
   */
  playerAllowVideoOnLowCapacity?: boolean
  /**
   * Digits that let a field technician leave Android kiosk (Lock Task) on this tenant's screens.
   * Mirrors tenant.settings.playerFieldServicePin; omitted or '' means unlock is not gated.
   * Operational lock against casual tampering, not a credential — never log it.
   */
  fieldServicePin?: string
  /**
   * Studio's Android kiosk lock switch. Mirrors tenant.settings.playerAndroidKioskLockEnabled;
   * omitted or true means Android Device Owner screens arm hard kiosk; false means they leave/stay
   * out of Lock Task regardless of Device Owner provisioning.
   */
  androidKioskLockEnabled?: boolean
  /** Tenant allows on-device audience analytics (aggregates). Mirrors tenant.featureFlags.audienceAnalytics. */
  audienceAnalytics?: boolean
  /**
   * Tenant allows COCO-SSD person metrics on heartbeats (super-admin flag). Mirrors tenant.featureFlags.audiencePersonCvAnalytics.
   */
  audiencePersonCvAnalytics?: boolean
  /** Tenant allows operator WebRTC preview of the audience camera. Mirrors tenant.featureFlags.audienceWebcamPreview. */
  audienceWebcamPreview?: boolean
  /**
   * Android Media3 hybrid progressive playback (under WebView). Product default on.
   * Mirrors tenant.featureFlags.nativePlaybackSurface (false = kill-switch). Player still requires APK ≥ 0.0.13.
   */
  nativePlaybackSurface?: boolean
  /**
   * ICE servers for audience WebRTC (STUN/TURN), from worker env `AUDIENCE_WEBRTC_ICE_SERVERS` + defaults.
   * Omitted when webcam preview is off or when unchanged from client defaults.
   */
  audienceWebRtcIceServers?: TAudienceWebRtcIceServer[]
  /** Idle-screen logo and background for registered players. */
  playerBranding?: ITenantPlayerBranding
}

/** Channel metadata sent to players. */
export interface IChannelPayloadChannel {
  id: string
  name: string
  description: string | null
}

/**
 * Playback schedule root + playback mode: everything {@link resolvePlaybackSegments}
 * needs to materialize the frame loop. Schedule roots with full metadata extend this; synthetic shells
 * from master playlists are also valid roots (playlist architecture checklist #8).
 */
export interface IPlaybackScheduleRoot {
  playbackMode: PlaybackMode
  customSequence: string[] | null
  playlists: IChannelPayloadPlaylistAssignment[]
  /**
   * Frames for playlists referenced via `playlistRef` in any frame of `playlists`.
   * Keyed by playlist URN. Only included when embedded playlist frames exist.
   */
  embeddedPlaylistFrames?: Record<string, TFrame[]>
  /** Root-level widget overrides merged into queue frames (tier 4 when the root is a legacy campaign). */
  widgetConfig?: Record<string, unknown> | null
}

/** Schedule root on a channel (operator campaign or playlist-only master). */
export interface IScheduleRootAssignment extends IPlaybackScheduleRoot {
  /**
   * Stable identity for this schedule root (today: campaign URN or master playlist URN from the tenant DO).
   * Older API responses used `campaignUrn` for the same value; {@link coerceRegistrationScheduleRoots} normalizes that.
   */
  scheduleRootUrn: string
  title: string
  /** Campaign version (0 = draft; incremented when campaign is or becomes published). Always sent by API. */
  version: number
  description: string | null
  /** Channel-level date range: when this campaign is active on this channel. */
  startDate: string | null
  endDate: string | null
  /** URL widget URNs in this campaign: template and dimensions so the player does not fetch binary. */
  widgetAssetMetadata?: Record<string, { urlTemplate: string, width?: number, height?: number, singletonCapable?: boolean, paramKeysOmitWhenEmpty?: string[], paramKeysFlag?: string[], fixedDefaultConfig?: Record<string, unknown>, defaultConfig?: Record<string, unknown> }>
  /** HLS video URNs in this campaign: manifest URL (and optional poster) so the player does not fetch binary. */
  hlsVideoAssetMetadata?: Record<string, { hlsUrl: string, posterUrl?: string }>
}

/** Playlist assignment within a campaign (schedule + content). */
export interface IChannelPayloadPlaylistAssignment {
  id: string
  playlistUrn: string
  /** Order in campaign (for Sequential/Interleave/Interpolate playback). */
  index: number
  title: string
  frames: TFrame[]
  /** Campaign-level date range for this playlist. */
  startDate: string | null
  endDate: string | null
  timeBlocks: ITimeBlock[]
  playWithoutInterruption: boolean
  /** Playlist-level widget config (tier 2). Merged with facet and campaign; campaign wins. */
  widgetConfig?: Record<string, unknown> | null
  /** Published playlist metadata (opaque key/value; consumed by specific features). */
  metadata?: Record<string, unknown>
  /**
   * When set by {@link combineRegistrationScheduleRootsForPlayback}, controls whether this row
   * may ignore `timeBlocks` as the operator screen-master slot. `true` only for rows copied from
   * the master schedule root; `false` when a non-master root reuses the master playlist URN.
   */
  operatorMasterTimeBlockBypass?: boolean
  /**
   * Optional distribution-list assignment geofilter (point + radius). Null/omitted = no geo gate.
   * Evaluated with live GPS → venue → {@link geoFallbackPoint}.
   */
  geoFilter?: import('./playlist-assignment-geo-filter').TPlaylistAssignmentGeoFilter | null
  /**
   * Venue lat/lng stamped at registration so the player can fall back without a venue lookup.
   * Only meaningful when {@link geoFilter} is set.
   */
  geoFallbackPoint?: import('./playlist-assignment-geo-filter').TGeoLatLng | null
}

/**
 * One registration playback row: playlist assignment + playback context (flat wire shape).
 * Registration-status exposes top-level `playlists[]` (no nested `schedule[]`).
 * Root identity is {@link scheduleRootUrn} (falls back to {@link playlistUrn} for older payloads).
 * {@link startDate}, {@link endDate}, and {@link widgetConfig} are the effective channel window and
 * root widget overrides for this row.
 */
export interface IPlayerRegistrationPlaylistEntry extends IChannelPayloadPlaylistAssignment {
  /**
   * Schedule-root URN this row belongs to (master shell or list-attached root). Required on new
   * API responses so multi-child masters regroup correctly; older clients may omit it.
   */
  scheduleRootUrn?: string
  /**
   * Channel-level flight window for the schedule root (distribution-list assignment dates).
   * Distinct from {@link startDate}/{@link endDate}, which stay playlist/embedding-level so
   * flatten→group round-trips do not collapse tighter child windows into the root range.
   */
  scheduleRootStartDate?: string | null
  scheduleRootEndDate?: string | null
  /**
   * Published revision of the schedule-root playlist document (`playlist_versions.version` on the root URN),
   * not a separate id. Drives {@link IPlayerRegistrationPayload.scheduleRevision} bumps with root URNs.
   */
  version: number
  playbackMode: PlaybackMode
  /** Present when this row uses `playlistRef` frames; keyed by embedded playlist URN. */
  embeddedPlaylistFrames?: Record<string, TFrame[]>
  widgetAssetMetadata?: IScheduleRootAssignment['widgetAssetMetadata']
  hlsVideoAssetMetadata?: IScheduleRootAssignment['hlsVideoAssetMetadata']
}

/**
 * Legacy registration-status nested `playerGroup` object (full row).
 * @deprecated Prefer top-level {@link IPlayerRegistrationPayload.distributionLists}; {@link parseRegistrationStatus} may synthesize this from the first list for downstream compatibility.
 */
export interface IPlayerGroupRegistrationPayload {
  id: string
  name: string
  description: string | null
  superseedsPlayerMaster: boolean
  simpleFeedCanonicalGroup: boolean
  masterPlaylistUrn?: string | null
  simpleFeedPlaylistUrn?: string | null
  tags?: Array<{ tagId: string }>
}

/**
 * Player registration status: tenant association and distribution list membership.
 *
 * - Not registered: use /registration with registration code
 * - Registered: tenant, `distributionLists` (may be empty), {@link IPlayerRegistrationPayload.playlists} from union of playlists on those lists (plus ungrouped paths when applicable).
 */
export interface IPlayerRegistrationPayload {
  tenant: ITenantInfo
  title: string
  description: string | null
  /** Distribution lists this screen belongs to (id + name); may be empty. */
  distributionLists?: Array<{ id: string, name: string, superseedsPlayerMaster?: boolean }>
  /** Operator-assigned per-screen master playlist URN (ordering hint for multi-root playback). */
  assignedMasterPlaylistUrn?: string | null
  /**
   * @deprecated Synthesized from the first {@link distributionLists} entry or parsed from legacy nested `playerGroup` in registration-status. Worker/UI still key change detection off this until fully migrated.
   */
  playerGroup?: IPlayerGroupRegistrationPayload | null
  /**
   * Flat playback rows (one per schedule slot). Order: optional per-screen master first, then each
   * distribution-list playlist (`sort_order`). Use {@link getPlayerRegistrationSchedule} to group by root for playback.
   */
  playlists?: IPlayerRegistrationPlaylistEntry[]
  /**
   * @deprecated Removed from API responses; legacy snapshots may still store nested roots. Prefer {@link playlists}.
   */
  schedule?: IScheduleRootAssignment[]
  /**
   * Increments when the server’s resolved schedule digest changes; clients may skip refetching full registration
   * when this matches the last applied value.
   */
  scheduleRevision?: number
  /** When set (object), a newer web OTA bundle is available; when null, server explicitly offers none; when undefined, key was missing from response. */
  webOta?: IWebOtaUpdateEventPayload | null
  /** Server wall time (ms) when response was built; optional, for sync offset. */
  serverTimeMs?: number
  /**
   * When present, server mandates overlay visibility until the device reports a matching heartbeat.
   * Only keys with an active mandate are included; omitted keys mean no server request for that overlay.
   */
  remoteOverlayUi?: TRemoteOverlayUi
  /** Monotonic seq for `remoteOverlayUi`; player applies only when this increases (replay safety). */
  overlayUiDirectiveSeq?: number
  /**
   * Monotonic seq from management app “repair asset cache”; player runs IndexedDB scan + campaign reload when this exceeds the last applied seq stored locally.
   */
  assetCacheRepairDirectiveSeq?: number
  /** Server-stored opt-in for audience analytics (CV aggregates). */
  audienceAnalyticsEnabled?: boolean
  /** Server-stored preferred audience camera device id (`MediaDeviceInfo.deviceId`). */
  audienceVideoInputDeviceId?: string | null
  /**
   * Weekly windows when the screen may play scheduled content. Omitted or null = no restriction.
   * Empty `blocks` = no restriction. When blocks exist and the current **device-local** instant is outside every block,
   * the player stops schedule playback and shows a black screen (status/timer overlays unchanged).
   */
  availabilityWeeklySchedule?: TPlayerWeeklyAvailabilitySchedule | null
  /**
   * Global advertiser-portal `ad_order.id` when this screen is on a distribution list tied to that order
   * (`distribution_list.portal_ad_order_id`). Sent on proof-of-play heartbeats as {@link IPlaybackHeartbeatBody.orderId}.
   */
  proofOfPlayOrderId?: string | null
  /** Screen venue when assigned; used for CTA URL `[[venueId]]` interpolation. */
  venueId?: string | null
  /** Tenant storefront handle when configured; used for CTA URL `[[tenantHandle]]` interpolation. */
  tenantHandle?: string | null
  /**
   * Tenant CTA catalog entries referenced by scheduled playlist frames (`ctaId` → template URL).
   * Placeholders in `url` are expanded on the player at QR render time.
   */
  ctaRegistry?: Record<string, { id: string, name: string, url: string }>
}

/** Returns registration schedule roots grouped from flat `playlists` or legacy nested `schedule`. */
export function getPlayerRegistrationSchedule(
  payload: IPlayerRegistrationPayload | null
): IScheduleRootAssignment[] {
  if (payload === null) {
    return []
  }
  if (payload.playlists != null) {
    return coerceRegistrationScheduleRoots(groupRegistrationPlaylistsToScheduleRoots(payload.playlists))
  }
  if (payload.schedule != null) {
    return coerceRegistrationScheduleRoots(payload.schedule)
  }
  const legacy = payload.playerGroup as unknown as {
    campaigns?: IScheduleRootAssignment[]
    scheduleRoots?: IScheduleRootAssignment[]
  } | null
  return legacy?.scheduleRoots ?? legacy?.campaigns ?? []
}

/**
 * Normalizes schedule roots from API or persisted JSON: copies deprecated `campaignUrn` into `scheduleRootUrn` and drops the legacy key.
 */
export function coerceRegistrationScheduleRoots(
  roots: ReadonlyArray<IScheduleRootAssignment & { campaignUrn?: string }>
): IScheduleRootAssignment[] {
  return roots.map((item) => {
    const ext = item as IScheduleRootAssignment & { campaignUrn?: string }
    const scheduleRootUrn = (typeof ext.scheduleRootUrn === 'string' && ext.scheduleRootUrn !== '')
      ? ext.scheduleRootUrn
      : (typeof ext.campaignUrn === 'string' && ext.campaignUrn !== '')
          ? ext.campaignUrn
          : ''
    if (scheduleRootUrn === '') {
      return ext
    }
    const { campaignUrn: _legacyCampaignUrn, ...rest } = ext
    return { ...rest, scheduleRootUrn } as IScheduleRootAssignment
  })
}

/** Response from GET /players/:id/time for clock sync. Echo clientSendMs when provided for RTT-corrected offset. */
export interface IServerTimeResponse {
  serverTimeMs: number
  /** Echo of request query clientSendMs when provided. */
  clientSendMs?: number
}
